/**
 * FileDropzone — a drag-and-drop area for picking files from the user's machine.
 *
 * Selection only: nothing is uploaded here. The parent owns the chosen files and
 * decides what to send and when, so the same box serves a page that uploads on
 * pick and one that waits for a button.
 *
 * Files the box would refuse — wrong type, too large, a repeat of one already
 * chosen — are listed under it with the reason, rather than dropped silently or
 * raised with alert(): a user who dragged in five files should see which one did
 * not make it and why.
 */
import { useCallback, useId, useRef, useState } from 'react';
import { Icon } from './icon';

export interface FileDropzoneProps {
  /** The files currently chosen. Owned by the parent. */
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** MIME types and/or extensions, as for <input accept>, e.g. ['application/pdf', '.pdf']. */
  accept?: string[];
  /** Human list of accepted types for the hint, e.g. 'PDF, JPG, PNG'. */
  acceptLabel?: string;
  /** Per-file limit in MB. */
  maxSizeMb?: number;
  multiple?: boolean;
  disabled?: boolean;
  testId?: string;
}

interface Rejection {
  name: string;
  reason: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Same identity rule the browser gives the user: a file picked twice is the
// same name, size and modification time.
const sameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const matchesAccept = (file: File, accept: string[]): boolean => {
  if (accept.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return accept.some((rule) => {
    const r = rule.trim().toLowerCase();
    if (r.startsWith('.')) return name.endsWith(r);
    if (r.endsWith('/*')) return type.startsWith(r.slice(0, -1));
    return type === r;
  });
};

const FileDropzone = ({
  files,
  onFilesChange,
  accept = [],
  acceptLabel,
  maxSizeMb,
  multiple = true,
  disabled = false,
  testId = 'FileDropzone',
}: FileDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<Rejection[]>([]);

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming);
      if (list.length === 0) return;
      const accepted: File[] = [];
      const refused: Rejection[] = [];
      const limit = maxSizeMb ? maxSizeMb * 1024 * 1024 : null;

      for (const file of multiple ? list : list.slice(0, 1)) {
        if (!matchesAccept(file, accept)) {
          refused.push({ name: file.name, reason: `not an accepted type${acceptLabel ? ` (${acceptLabel})` : ''}` });
        } else if (limit && file.size > limit) {
          refused.push({ name: file.name, reason: `${formatSize(file.size)} is over the ${maxSizeMb} MB limit` });
        } else if (file.size === 0) {
          refused.push({ name: file.name, reason: 'the file is empty' });
        } else if (multiple && [...files, ...accepted].some((f) => sameFile(f, file))) {
          refused.push({ name: file.name, reason: 'already added' });
        } else {
          accepted.push(file);
        }
      }
      if (!multiple && list.length > 1) {
        refused.push({ name: `${list.length - 1} more file(s)`, reason: 'only one file can be added' });
      }

      setRejected(refused);
      if (accepted.length) onFilesChange(multiple ? [...files, ...accepted] : accepted);
    },
    [accept, acceptLabel, files, maxSizeMb, multiple, onFilesChange],
  );

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) addFiles(e.dataTransfer.files);
  };

  const removeAt = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  // Single-file mode shows its file inside the box rather than in a list.
  const selected = !multiple ? files[0] : undefined;

  const hint = [
    acceptLabel,
    maxSizeMb ? `up to ${maxSizeMb} MB${multiple ? ' each' : ''}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-describedby={hintId}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          // Also fired when moving onto the icon or text inside the box; only a
          // move to outside it ends the drag, or the highlight flickers.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={onDrop}
        className={[
          'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-12 text-center transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          disabled
            ? 'cursor-not-allowed border-line bg-canvas opacity-60'
            : dragging
              ? 'cursor-copy border-primary bg-indigo-50 dark:bg-indigo-900/20'
              : 'cursor-pointer border-line bg-surface hover:border-primary hover:bg-indigo-50 dark:hover:bg-indigo-900/20',
        ].join(' ')}
        data-testid={`${testId}.Area`}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-primary dark:bg-indigo-900/30">
          <Icon name={selected ? 'file-text' : 'upload'} size={24} />
        </span>
        {selected && !dragging ? (
          // Single-file mode: the chosen file is shown in the box itself, and
          // dropping or browsing again replaces it.
          <div className="flex w-full max-w-md flex-col items-center gap-1" data-testid={`${testId}.Selected`}>
            <div className="flex max-w-full items-center gap-2">
              <p className="truncate text-body text-ink" title={selected.name}>
                Selected file: <span className="font-medium">{selected.name}</span>
              </p>
              <button
                type="button"
                aria-label={`Remove ${selected.name}`}
                onClick={(e) => {
                  // Inside the clickable box: without this the click would also
                  // open the file picker.
                  e.stopPropagation();
                  onFilesChange([]);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                disabled={disabled}
                className="shrink-0 rounded p-1 text-ink-muted transition-colors hover:bg-red-50 hover:text-error dark:hover:bg-red-900/20 disabled:opacity-50"
                data-testid={`${testId}.Remove`}
              >
                <Icon name="x" size={16} />
              </button>
            </div>
            <p id={hintId} className="text-caption text-ink-muted">
              {formatSize(selected.size)} · drop another file or <span className="text-primary">browse</span> to replace it
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium text-ink">
              {dragging ? (selected ? 'Drop to replace' : 'Drop to add') : (
                <>Drag and drop {multiple ? 'files' : 'a file'} here, or <span className="text-primary">browse</span></>
              )}
            </p>
            {hint && <p id={hintId} className="text-caption text-ink-muted">{hint}</p>}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept.join(',')}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            // Cleared so picking the same file again after removing it still fires.
            e.target.value = '';
          }}
          data-testid={`${testId}.Input`}
        />
      </div>

      {rejected.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20"
          data-testid={`${testId}.Rejected`}
        >
          {rejected.map((r) => (
            <p key={`${r.name}:${r.reason}`} className="text-body-sm text-error">
              <span className="font-medium">{r.name}</span> — {r.reason}
            </p>
          ))}
        </div>
      )}

      {multiple && files.length > 0 && (
        <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface" data-testid={`${testId}.List`}>
          {files.map((file, index) => (
            <li key={`${file.name}:${file.size}:${file.lastModified}`} className="flex items-center gap-3 px-3 py-2">
              <Icon name="file-text" size={18} className="shrink-0 text-ink-muted" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-ink" title={file.name}>{file.name}</p>
                <p className="text-caption text-ink-muted">{formatSize(file.size)}</p>
              </div>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeAt(index)}
                disabled={disabled}
                className="rounded p-1.5 text-ink-muted transition-colors hover:bg-red-50 hover:text-error dark:hover:bg-red-900/20 disabled:opacity-50"
                data-testid={`${testId}.Remove`}
              >
                <Icon name="x" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileDropzone;
